import * as React from "react";
import clsx from "clsx";
import { ChevronDown, Save } from "lucide-react";
import { Button } from "../../../ui/components/Button";
import { Callout } from "../../../ui/components/Callout";
import { Text } from "../../../ui/components/Text";
import * as formStyles from "../../../ui/components/Form/FormInput.style.css";
import { getApiErrorMessage } from "../../lib/api/api-error";
import type { Language } from "../../lib/api/localization";
import {
  updateProfile,
  updateUserLocale,
  type User,
} from "../../lib/api/users";
import { useI18n } from "../../lib/i18n";
import { setStoredTimeZone } from "../../lib/i18n/storage";
import {
  detectBrowserTimeZone,
  formatTimeZoneLabel,
  listTimeZones,
} from "../../lib/locale";
import * as styles from "../layouts/ProfilePage/ProfilePage.style.css";
import * as localeStyles from "./LocalePreferencesSection.style.css";

export type LocalePreferencesController = {
  dirty: boolean;
  /** Count of dirty fields (language / timezone). */
  changeCount: number;
  saving: boolean;
  save: () => Promise<void>;
  reset: () => void;
};

export type LocalePreferencesSectionProps = {
  accessToken: string;
  /** Required when mode is "admin". */
  userId?: string;
  mode: "self" | "admin";
  preferredLanguage?: string | null;
  preferredTimezone?: string | null;
  onSaved?: (user?: User) => void | Promise<void>;
  /**
   * When true, omit section header + inline save/badge chrome.
   * Parent drives save via `onStateChange` (e.g. DetailPageLayout floating bar).
   */
  hideChrome?: boolean;
  /** Reports dirty/save/reset so a parent layout can drive floating actions. */
  onStateChange?: (state: LocalePreferencesController) => void;
};

function SectionHeader({
  title,
  subtitle,
}: {
  title: string;
  subtitle?: string;
}) {
  return (
    <div className={styles.sectionHeader}>
      <Text level="h3" weight="bold">
        {title}
      </Text>
      {subtitle ? (
        <Text level="body-medium" className={styles.sectionSubtitle}>
          {subtitle}
        </Text>
      ) : null}
    </div>
  );
}

function LanguageFlag({ flagUrl }: { flagUrl?: string | null }) {
  if (flagUrl) {
    return <img src={flagUrl} alt="" className={localeStyles.flag} />;
  }
  return <span className={localeStyles.flagPlaceholder} aria-hidden />;
}

function LanguageSelect({
  id,
  label,
  value,
  options,
  changed,
  disabled,
  onChange,
}: {
  id: string;
  label: React.ReactNode;
  value: string;
  options: Language[];
  changed: boolean;
  disabled?: boolean;
  onChange: (code: string) => void;
}) {
  const [open, setOpen] = React.useState(false);
  const wrapRef = React.useRef<HTMLDivElement>(null);
  const selected =
    options.find((l) => l.code === value) ?? options[0] ?? null;

  React.useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className={formStyles.wrapper}>
      <label htmlFor={id} className={formStyles.label}>
        {label}
      </label>
      <div className={localeStyles.selectWrap} ref={wrapRef}>
        <button
          id={id}
          type="button"
          className={clsx(
            localeStyles.selectTrigger,
            changed && localeStyles.selectTriggerChanged,
          )}
          disabled={disabled || options.length === 0}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-label={
            selected
              ? `${selected.name} (${selected.code})`
              : String(label)
          }
          onClick={() => setOpen((v) => !v)}
        >
          <LanguageFlag flagUrl={selected?.flagUrl} />
          <span className={localeStyles.selectTriggerLabel}>
            {selected
              ? `${selected.name} (${selected.code})`
              : "—"}
          </span>
          <ChevronDown size={16} className={localeStyles.chevron} aria-hidden />
        </button>
        {open ? (
          <div className={localeStyles.menu} role="listbox">
            {options.map((lang) => (
              <button
                key={lang.code}
                type="button"
                role="option"
                aria-selected={lang.code === value}
                className={clsx(
                  localeStyles.menuItem,
                  lang.code === value && localeStyles.menuItemActive,
                )}
                onClick={() => {
                  onChange(lang.code);
                  setOpen(false);
                }}
              >
                <LanguageFlag flagUrl={lang.flagUrl} />
                <span>
                  {lang.name} ({lang.code})
                </span>
              </button>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}

export function LocalePreferencesSection({
  accessToken,
  userId,
  mode,
  preferredLanguage,
  preferredTimezone,
  onSaved,
  hideChrome = false,
  onStateChange,
}: LocalePreferencesSectionProps) {
  const { t, tx, language, languages, setLanguage } = useI18n();
  const preserveMessageRef = React.useRef(false);

  const languageOptions = React.useMemo(
    () => languages.filter((l) => l.enabled),
    [languages],
  );

  const profileTimezone = React.useMemo(() => {
    if (preferredTimezone != null && String(preferredTimezone).trim()) {
      return String(preferredTimezone).trim();
    }
    return null;
  }, [preferredTimezone]);

  const hasPersistedTimezone = profileTimezone != null;

  const initialLanguage = React.useMemo(() => {
    const fromProfile =
      preferredLanguage != null && String(preferredLanguage).trim()
        ? String(preferredLanguage).trim().toLowerCase()
        : null;
    if (fromProfile && languageOptions.some((l) => l.code === fromProfile)) {
      return fromProfile;
    }
    if (mode === "self") return language;
    return languageOptions.find((l) => l.isDefault)?.code ?? languageOptions[0]?.code ?? "en";
  }, [preferredLanguage, languageOptions, language, mode]);

  // Draft default when unset — shown in the UI but not treated as a saved preference.
  const initialTimezone = React.useMemo(
    () => profileTimezone || detectBrowserTimeZone() || "UTC",
    [profileTimezone],
  );

  const [langValue, setLangValue] = React.useState(initialLanguage);
  const [tzValue, setTzValue] = React.useState(initialTimezone);
  const [saving, setSaving] = React.useState(false);
  const [message, setMessage] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    setLangValue(initialLanguage);
    setTzValue(initialTimezone);
    setError(null);
    if (preserveMessageRef.current) {
      preserveMessageRef.current = false;
      return;
    }
    setMessage(null);
  }, [initialLanguage, initialTimezone]);

  // Clear stale success when switching the admin target user.
  React.useEffect(() => {
    setMessage(null);
  }, [userId, mode]);

  const timeZones = React.useMemo(() => {
    const list = listTimeZones();
    if (tzValue && !list.includes(tzValue)) {
      return [tzValue, ...list];
    }
    return list;
  }, [tzValue]);

  const languageDirty = langValue !== initialLanguage;
  const timezoneDirty = tzValue !== initialTimezone;
  const dirty = languageDirty || timezoneDirty;
  const changeCount = (languageDirty ? 1 : 0) + (timezoneDirty ? 1 : 0);

  const reset = React.useCallback(() => {
    setLangValue(initialLanguage);
    setTzValue(initialTimezone);
    setError(null);
    setMessage(null);
  }, [initialLanguage, initialTimezone]);

  const save = React.useCallback(async () => {
    if (!dirty || saving) return;
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      // Persist the draft browser timezone on first save even if only language changed.
      const shouldPersistTimezone =
        timezoneDirty || !hasPersistedTimezone;

      if (mode === "self") {
        if (languageDirty) {
          await setLanguage(langValue);
        }
        if (shouldPersistTimezone) {
          const updated = await updateProfile(accessToken, {
            preferredTimezone: tzValue,
          });
          setStoredTimeZone(tzValue);
          preserveMessageRef.current = true;
          await onSaved?.(updated);
        } else {
          preserveMessageRef.current = true;
          await onSaved?.();
        }
        setMessage(t("pro.locale.updated"));
      } else {
        if (!userId) throw new Error("User id is required");
        const patch: {
          preferredLanguage?: string;
          preferredTimezone?: string;
        } = {};
        if (languageDirty) patch.preferredLanguage = langValue;
        if (shouldPersistTimezone) patch.preferredTimezone = tzValue;
        const updated = await updateUserLocale(accessToken, userId, patch);
        preserveMessageRef.current = true;
        await onSaved?.(updated);
        setMessage(t("pro.locale.updated"));
      }
    } catch (err) {
      preserveMessageRef.current = false;
      setError(getApiErrorMessage(err, t("pro.locale.failedUpdate")));
      throw err;
    } finally {
      setSaving(false);
    }
  }, [
    dirty,
    saving,
    timezoneDirty,
    hasPersistedTimezone,
    mode,
    languageDirty,
    setLanguage,
    langValue,
    accessToken,
    tzValue,
    onSaved,
    t,
    userId,
  ]);

  // Keep latest save/reset in refs so notifying the parent doesn't depend on
  // callback identity (avoids infinite update loops when parent re-renders).
  const saveRef = React.useRef(save);
  const resetRef = React.useRef(reset);
  saveRef.current = save;
  resetRef.current = reset;

  React.useEffect(() => {
    if (!onStateChange) return;
    onStateChange({
      dirty,
      changeCount,
      saving,
      save: () => saveRef.current(),
      reset: () => resetRef.current(),
    });
  }, [onStateChange, dirty, changeCount, saving]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await save();
    } catch {
      /* error already set */
    }
  };

  const fields = (
    <>
      {error && <Callout variant="error">{error}</Callout>}
      {message && !dirty && <Callout variant="success">{message}</Callout>}

      <div className={styles.nameRow}>
        <LanguageSelect
          id="locale-language"
          label={tx("pro.locale.language")}
          value={langValue}
          options={languageOptions}
          changed={languageDirty}
          disabled={saving || languageOptions.length === 0}
          onChange={(code) => {
            setLangValue(code);
            setMessage(null);
          }}
        />

        <div className={formStyles.wrapper}>
          <label htmlFor="locale-timezone" className={formStyles.label}>
            {tx("pro.locale.timezone")}
          </label>
          <select
            id="locale-timezone"
            value={tzValue}
            onChange={(e) => {
              setTzValue(e.target.value);
              setMessage(null);
            }}
            className={clsx(
              formStyles.input,
              timezoneDirty && formStyles.inputChanged,
            )}
            disabled={saving}
          >
            {timeZones.map((tz) => (
              <option key={tz} value={tz}>
                {formatTimeZoneLabel(tz)}
              </option>
            ))}
          </select>
        </div>
      </div>
    </>
  );

  if (hideChrome) {
    return <div className={styles.cardInner}>{fields}</div>;
  }

  return (
    <form onSubmit={handleSubmit}>
      <div className={styles.cardInner}>
        <SectionHeader
          title={t("pro.locale.title")}
          subtitle={
            mode === "self"
              ? t("pro.locale.subtitleSelf")
              : t("pro.locale.subtitleAdmin")
          }
        />

        {fields}

        <div
          className={clsx(
            styles.cardActions,
            !dirty && styles.cardActionsEnd,
          )}
        >
          {dirty ? (
            <span className={styles.changedBadge}>
              {tx("pro.locale.unsavedChanges")}
            </span>
          ) : null}
          <Button
            type="submit"
            color="primary"
            variant="solid"
            iconLeft={<Save size={16} />}
            loading={saving}
            disabled={!dirty}
          >
            {tx("pro.locale.save")}
          </Button>
        </div>
      </div>
    </form>
  );
}
